import type { ChangeEvent } from 'react';
import { useCallback } from 'react';

import LinkOutlined from '@mui/icons-material/LinkOutlined';
import { Box, CircularProgress, TextField, Typography } from '@mui/material';

import Button from '@/components/Buttons/Button';
import useShareButton from '@/components/ShareButton/useShareButton';
import ShareDialog from '@/components/ShareDialog';
import { useGenerateShareLink } from '@/features/Dashboard/api/mutations';
import {
  SHARE_DASHBOARD_EMAIL_TEMPLATE,
  SHARED_DASHBOARD_PATH
} from '@/features/Dashboard/constants/const';
import copyToClipboard from '@/utils/copyToClipboard';
import { emailValidator } from '@/utils/validators';

const GenerateLinkSection = ({ dashboardName }: { dashboardName: string }) => {
  const { data: generatedUrl, isPending, mutate } = useGenerateShareLink(dashboardName);

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 3 }}>
        <LinkOutlined sx={{ mr: 1 }} />
        <Typography>Share a link</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <TextField
          hiddenLabel
          placeholder='Link will appear here...'
          sx={{
            fontSize: 14,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textWrap: 'nowrap',
            flexGrow: 1,
            '& .MuiFilledInput-input': {
              py: '14px!important'
            },
            '& .MuiInputBase-root': {
              backgroundColor: '#4F545873'
            }
          }}
          value={generatedUrl ?? ''}
          variant='filled'
        />
        {generatedUrl ? (
          <Button
            color='primary'
            sx={{ ml: 1, height: 48, width: 120, minWidth: 120, whiteSpace: 'nowrap' }}
            variant='contained'
            onClick={() => copyToClipboard(generatedUrl, 'Link copied to clipboard!')}
          >
            Copy link
          </Button>
        ) : (
          <Button
            color='primary'
            disabled={isPending}
            sx={{
              ml: 1,
              height: 48,
              width: 120,
              minWidth: 120,
              whiteSpace: 'nowrap',
              '&.Mui-disabled': {
                color: 'white',
                opacity: 1,
                background:
                  'linear-gradient(0deg, rgba(0, 0, 0, 0.35) 0%, rgba(0, 0, 0, 0.35) 100%), #E84F0F'
              }
            }}
            variant='contained'
            onClick={() => {
              mutate();
            }}
          >
            {isPending ? (
              <CircularProgress
                size={20}
                sx={{ color: 'white' }}
              />
            ) : (
              'Generate link'
            )}
          </Button>
        )}
      </Box>
    </>
  );
};

export const DashboardShareDialog = ({
  open,
  title,
  onClose
}: {
  open: boolean;
  title: string;
  onClose: () => void;
}) => {
  const shareUrl = `${window.location.origin}${SHARED_DASHBOARD_PATH}`;

  const { content, setContent, recipient, handleChangeRecipient, sendEmail, emailError } =
    useShareButton(undefined, shareUrl, {
      dashboardName: title,
      template: SHARE_DASHBOARD_EMAIL_TEMPLATE
    });

  const handleSendEmail = useCallback(() => {
    if (emailValidator(recipient)) return;
    onClose();
    sendEmail();
  }, [sendEmail, onClose, recipient]);

  const handleContentChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setContent(e.target.value);
    },
    [setContent]
  );

  return (
    <ShareDialog
      content={content}
      emailError={emailError}
      handleChangeRecipient={handleChangeRecipient}
      header={<Typography sx={{ fontSize: 20, fontWeight: 700 }}>Share {title}</Typography>}
      open={open}
      recipient={recipient}
      renderLinkSection={<GenerateLinkSection dashboardName={title} />}
      shareUrl={shareUrl}
      onClose={onClose}
      onContentChange={handleContentChange}
      onSendEmail={handleSendEmail}
    />
  );
};
